import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Icon } from '@iconify/react/dist/iconify.js';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

// Embeddable Zone -> Circle -> Division tree with per-node complaint counts and
// lazy-loaded complaint details. Used inline inside the dashboard Total card.
const LEVEL_META = {
  zone: { color: '#f59e0b', icon: 'mdi:map-marker-radius', label: 'Zone' },
  circle: { color: '#3b82f6', icon: 'mdi:map-marker-path', label: 'Circle' },
  division: { color: '#22c55e', icon: 'mdi:map-marker', label: 'Division' },
};

const STATUS_COLOR = {
  Open: '#f59e0b',
  'In Progress': '#6366f1',
  Closed: '#0ea5e9',
  Escalated: '#ef4444',
  Reopened: '#14b8a6',
  Resolved: '#22c55e',
};
const statusColor = (s) => STATUS_COLOR[s] || '#64748b';
const statusLabel = (s) => (s === 'In Progress' ? 'Assigned' : s === 'Reopened' ? 'Re-Open' : s || '-');

const openReport = (id) => window.open(`/complains/view/${id}`, '_blank', 'noopener');

const ForestHierarchyTree = () => {
  const API_BASE_URL = process.env.REACT_APP_BE_API_URL;

  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [openList, setOpenList] = useState({});
  const [nodeData, setNodeData] = useState({});

  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API_BASE_URL}/backend/complaints/forest-hierarchy-overview`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.data.status) setTree(res.data.zones || []);
        else setError(res.data.message || 'Failed to load hierarchy.');
      } catch (err) {
        setError(err.response?.data?.message || err.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [API_BASE_URL]);

  const keyOf = (level, id) => `${level}:${id}`;
  const toggleExpand = (level, id) => {
    const k = keyOf(level, id);
    setExpanded((p) => ({ ...p, [k]: !p[k] }));
  };

  const loadComplaints = useCallback(async (level, id) => {
    const k = keyOf(level, id);
    setNodeData((p) => ({ ...p, [k]: { ...(p[k] || {}), loading: true } }));
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE_URL}/backend/complaints/complaints-by-forest-node`, {
        params: { level, id, perPage: 50, pageNo: 1 },
        headers: { Authorization: `Bearer ${token}` },
      });
      setNodeData((p) => ({ ...p, [k]: { loading: false, list: res.data.data || [], totalCount: res.data.totalCount || 0 } }));
    } catch (err) {
      setNodeData((p) => ({ ...p, [k]: { loading: false, list: [], totalCount: 0, error: true } }));
    }
  }, [API_BASE_URL]);

  const toggleList = (level, id) => {
    const k = keyOf(level, id);
    const willOpen = !openList[k];
    setOpenList((p) => ({ ...p, [k]: willOpen }));
    if (willOpen && !nodeData[k]) loadComplaints(level, id);
  };

  const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : '-');

  const ComplaintsPanel = ({ level, id }) => {
    const k = keyOf(level, id);
    const nd = nodeData[k];
    if (!openList[k]) return null;
    if (!nd || nd.loading) return <div style={{ padding: '6px 0' }}><Skeleton count={3} height={20} /></div>;
    if (!nd.list.length) return <div style={{ padding: '8px 4px', color: '#94a3b8', fontSize: 13 }}>No complaints in this {level}.</div>;
    return (
      <div style={{ overflowX: 'auto', margin: '4px 0 10px' }}>
        <table className="table table-sm mb-0" style={{ fontSize: 12.5 }}>
          <thead>
            <tr style={{ color: '#64748b' }}>
              <th>Complaint #</th><th>Date</th><th>Nature</th><th>Referred By</th><th>Complainant</th><th>Status</th><th></th>
            </tr>
          </thead>
          <tbody>
            {nd.list.map((c) => (
              <tr key={c._id} style={{ cursor: 'pointer' }} onClick={() => openReport(c._id)}>
                <td className="fw-semibold text-primary-600">{c.complaint_number}</td>
                <td>{fmtDate(c.date)}</td>
                <td>{c.nature || '-'}</td>
                <td>{c.referred_by || '-'}</td>
                <td>{c.complainant_name || '-'}</td>
                <td><span className="badge" style={{ background: `${statusColor(c.status)}1f`, color: statusColor(c.status), fontWeight: 700, whiteSpace: 'nowrap' }}>{statusLabel(c.status)}</span></td>
                <td><Icon icon="mdi:open-in-new" className="text-primary-600" /></td>
              </tr>
            ))}
          </tbody>
        </table>
        {nd.totalCount > nd.list.length && (
          <div style={{ fontSize: 12, color: '#94a3b8', padding: '4px' }}>Showing {nd.list.length} of {nd.totalCount}.</div>
        )}
      </div>
    );
  };

  const NodeRow = ({ level, node, hasChildren, depth }) => {
    const meta = LEVEL_META[level];
    const k = keyOf(level, node._id);
    const isExpanded = expanded[k];
    return (
      <div style={{ marginLeft: depth * 20 }}>
        <div className="d-flex align-items-center gap-2 py-2 px-2" style={{
          borderLeft: `3px solid ${meta.color}`, background: '#fff', borderRadius: 10,
          boxShadow: '0 1px 3px rgba(0,0,0,.05)', marginBottom: 8,
        }}>
          {hasChildren ? (
            <button type="button" className="btn btn-sm p-0" style={{ width: 22 }} onClick={() => toggleExpand(level, node._id)}>
              <Icon icon={isExpanded ? 'mdi:chevron-down' : 'mdi:chevron-right'} width="20" />
            </button>
          ) : <span style={{ width: 22 }} />}
          <span style={{ width: 28, height: 28, borderRadius: 8, background: `${meta.color}22`, color: meta.color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icon icon={meta.icon} />
          </span>
          <span style={{ fontSize: 10.5, fontWeight: 700, color: meta.color, textTransform: 'uppercase', letterSpacing: '.05em' }}>{meta.label}</span>
          <span className="fw-semibold">{node.name}</span>
          <div className="ms-auto d-flex align-items-center gap-2">
            <span style={{ background: `${meta.color}22`, color: meta.color, fontWeight: 800, fontSize: 12.5, borderRadius: 999, padding: '2px 12px', minWidth: 42, textAlign: 'center' }}>{node.count}</span>
            <button type="button" className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1" onClick={() => toggleList(level, node._id)}>
              <Icon icon="mdi:format-list-bulleted" /> {openList[k] ? 'Hide' : 'Complaints'}
            </button>
          </div>
        </div>
        <div style={{ marginLeft: 32 }}><ComplaintsPanel level={level} id={node._id} /></div>
      </div>
    );
  };

  if (loading) return <Skeleton count={6} height={48} style={{ marginBottom: 8 }} />;
  if (error) return <div className="alert alert-danger mb-0">{error}</div>;
  if (!tree.length) return <div className="text-center text-secondary-light py-4">No forest zones found.</div>;

  return (
    <div>
      {tree.map((zone) => (
        <div key={zone._id} className="mb-1">
          <NodeRow level="zone" node={zone} hasChildren={zone.circles?.length > 0} depth={0} />
          {expanded[keyOf('zone', zone._id)] && (zone.circles || []).map((circle) => (
            <div key={circle._id}>
              <NodeRow level="circle" node={circle} hasChildren={circle.divisions?.length > 0} depth={1} />
              {expanded[keyOf('circle', circle._id)] && (circle.divisions || []).map((div) => (
                <NodeRow key={div._id} level="division" node={div} hasChildren={false} depth={2} />
              ))}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

export default ForestHierarchyTree;
